import React from 'react';

const STEPS = [
  {
    key: 'auto', label: 'Auto-scored controls', page: 'auto',
    desc: 'Run automated scan, review & confirm scores',
    color: '#2E7D32', bg: '#E8F5E9',
    progress: (w) => {
      if (!w?.auto) return { done: 0, total: 0, label: '' };
      const done = w.auto.confirmed;
      const total = w.auto.total;
      return { done, total, label: `${done}/${total} confirmed` };
    },
    isDone: (w) => w?.auto && w.auto.total > 0 && w.auto.confirmed >= w.auto.total,
  },
  {
    key: 'uploads', label: 'Document uploads', page: 'uploads',
    desc: 'Upload governance docs for AI-assisted scoring',
    color: '#6A1B9A', bg: '#F3E5F5',
    progress: (w) => {
      if (!w?.uploads) return { done: 0, total: 0, label: '' };
      return { done: w.uploads.scored, total: w.uploads.total, label: `${w.uploads.scored}/${w.uploads.total} scored` };
    },
    isDone: (w) => w?.uploads && w.uploads.total > 0 && w.uploads.scored >= w.uploads.total,
  },
  {
    key: 'interviews', label: 'Stakeholder interviews', page: 'interviews',
    desc: 'Score controls via structured interviews',
    color: '#455A64', bg: '#F1F3F5',
    progress: (w) => {
      if (!w?.interviews) return { done: 0, total: 0, label: '' };
      return { done: w.interviews.scored, total: w.interviews.total, label: `${w.interviews.scored}/${w.interviews.total} scored` };
    },
    isDone: (w) => w?.interviews && w.interviews.total > 0 && w.interviews.scored >= w.interviews.total,
  },
  {
    key: 'manual', label: 'Manual inputs', page: 'manual',
    desc: 'Score remaining controls from walkthroughs & observations',
    color: '#F57F17', bg: '#FFF8E1',
    progress: (w) => {
      if (!w?.manual) return { done: 0, total: 0, label: '' };
      return { done: w.manual.scored, total: w.manual.total, label: `${w.manual.scored}/${w.manual.total} scored` };
    },
    isDone: (w) => w?.manual && w.manual.total > 0 && w.manual.scored >= w.manual.total,
  },
  {
    key: 'evidence', label: 'Evidence review', page: 'evidence',
    desc: 'Verify all controls before report',
    color: '#FFA000', bg: '#FFF8E1',
    progress: (w) => {
      if (!w) return { done: 0, total: 0, label: '' };
      const total = (w.auto?.total||0) + (w.uploads?.total||0) + (w.interviews?.total||0) + (w.manual?.total||0);
      const done = (w.auto?.confirmed||0) + (w.uploads?.scored||0) + (w.interviews?.scored||0) + (w.manual?.scored||0);
      return { done, total, label: `${done}/${total} verified` };
    },
    isDone: (w) => {
      if (!w) return false;
      const total = (w.auto?.total||0) + (w.uploads?.total||0) + (w.interviews?.total||0) + (w.manual?.total||0);
      const done = (w.auto?.confirmed||0) + (w.uploads?.scored||0) + (w.interviews?.scored||0) + (w.manual?.scored||0);
      return total > 0 && done >= total;
    },
  },
  {
    key: 'report', label: 'Findings report', page: 'report',
    desc: 'Review findings and export PDF',
    color: '#1565C0', bg: '#E8F0FE',
    progress: () => ({ done: 0, total: 0, label: 'Final output' }),
    isDone: () => false,
  },
];

export default function WorkflowStepper({ currentPage, setPage, workflow }) {
  // Find the first incomplete step
  const firstIncompleteIdx = STEPS.findIndex(s => !s.isDone(workflow));
  const currentIdx = STEPS.findIndex(s => s.page === currentPage);

  return (
    <div style={{
      display: 'flex', alignItems: 'stretch', gap: 0,
      padding: '0 20px',
      background: '#fff',
      borderTop: '1px solid #E8ECF0',
      flexShrink: 0,
      minHeight: 56,
    }}>
      {STEPS.map((step, i) => {
        const prog = step.progress(workflow);
        const done = step.isDone(workflow);
        const isCurrent = step.page === currentPage;
        const isNext = i === firstIncompleteIdx && !isCurrent;
        const pct = prog.total > 0 ? Math.round((prog.done / prog.total) * 100) : 0;

        return (
          <div
            key={step.key}
            onClick={() => setPage(step.page)}
            style={{
              flex: 1,
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px',
              cursor: 'pointer',
              background: isCurrent ? '#F0F6FF' : 'transparent',
              borderBottom: isCurrent ? '3px solid #1565C0' : '3px solid transparent',
              borderRight: i < STEPS.length - 1 ? '1px solid #EEF0F3' : 'none',
              transition: 'all 0.15s',
              position: 'relative',
            }}
          >
            {/* Step circle */}
            <div style={{
              width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700,
              background: done ? '#2E7D32' : isCurrent ? '#1565C0' : step.bg,
              color: done ? '#fff' : isCurrent ? '#fff' : step.color,
              border: isNext ? `2px solid ${step.color}` : 'none',
            }}>
              {done ? '\u2713' : i + 1}
            </div>

            {/* Label + progress */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{
                  fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                  color: done ? '#2E7D32' : isCurrent ? '#1565C0' : '#0D1B2A',
                }}>
                  {step.label}
                </span>
                {isNext && (
                  <span style={{
                    fontSize: 8, fontWeight: 700, padding: '1px 6px', borderRadius: 6,
                    background: '#1565C0', color: '#fff', textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>
                    Next
                  </span>
                )}
              </div>

              {/* Mini progress bar */}
              {prog.total > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                  <div style={{
                    flex: 1, height: 4, borderRadius: 2,
                    background: '#EEF0F3', overflow: 'hidden', maxWidth: 80,
                  }}>
                    <div style={{
                      width: `${pct}%`, height: '100%', borderRadius: 2,
                      background: done ? '#2E7D32' : step.color,
                      transition: 'width 0.3s ease',
                    }} />
                  </div>
                  <span style={{ fontSize: 9, color: '#7A8899', whiteSpace: 'nowrap' }}>
                    {prog.label}
                  </span>
                </div>
              ) : (
                <div style={{ fontSize: 9, color: '#7A8899', marginTop: 2 }}>{prog.label}</div>
              )}
            </div>

            {/* Connector arrow */}
            {i < STEPS.length - 1 && (
              <div style={{
                position: 'absolute', right: -7, top: '50%', transform: 'translateY(-50%)',
                width: 0, height: 0,
                borderTop: '6px solid transparent',
                borderBottom: '6px solid transparent',
                borderLeft: `6px solid ${done ? '#A5D6A7' : '#E0E4E8'}`,
                zIndex: 1,
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}
